import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { RootState } from "../../../../../../../redux/store";
import AnalyticsIcon from "../../../../../../../assets/svg/analytics-icon";
import VerifiedIcon from "../../../../../../../assets/svg/VerifiedIcon";
import { ChartEmptyState } from "./ChartCard";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";

export interface RaisedVsClosedMonthData {
  month: string;
  year: string;
  raised: number;
  closed: number;
}

interface MonthlyRaisedVsClosedProps {
  data: RaisedVsClosedMonthData[];
}

type MonthTickProps = {
  x?: number;
  y?: number;
  payload?: { value: string };
  isDarkMode?: boolean;
  data?: RaisedVsClosedMonthData[];
};

const MonthTick = ({ x = 0, y = 0, payload, isDarkMode, data = [] }: MonthTickProps) => {
  const label = payload?.value ?? "";
  const entry = data?.find((d) => d?.month === label);
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={12}
        textAnchor="middle"
        fill={isDarkMode ? "#c0c2c5" : "#898989"}
        fontSize={11}
        fontWeight={500}
      >
        {label}
      </text>
      <text
        x={0}
        y={0}
        dy={24}
        textAnchor="middle"
        fill={isDarkMode ? "#898989" : "#a3a3a3"}
        fontSize={10}
        fontWeight={400}
      >
        {entry?.year}
      </text>
    </g>
  );
};

type ValueLabelProps = {
  x?: number;
  y?: number;
  width?: number;
  value?: number;
  isDarkMode?: boolean;
};

const ValueLabel = ({ x = 0, y = 0, width = 0, value, isDarkMode }: ValueLabelProps) => {
  if (!value) return null;
  return (
  <text
    x={x + width / 2}
    y={y - 6}
    textAnchor="middle"
    fill={isDarkMode ? "#fbfbfb" : "#252b37"}
    fontSize={12}
    fontWeight={500}
  >
    {value}
  </text>
  );
};

const MonthlyRaisedVsClosed = ({ data }: MonthlyRaisedVsClosedProps) => {
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  const isDarkMode = useSelector(
    (state: RootState) => state?.themeProvider?.isDarkMode
  );
  const gridStroke = isDarkMode ? "#393f4a" : "#e9eaeb";
  const raisedFill = isDarkMode ? "#fbfbfb" : "#252b37";

  const maxValue = useMemo(() => {
    if (!data?.length) return 10;
    const max = Math.max(...data.map((d) => Math.max(d.raised, d.closed)));
    if (max <= 5) return 5;
    if (max <= 10) return 10;
    const rawStep = Math.pow(10, Math.floor(Math.log10(max))) / 2;
    const step = Math.max(5, Math.ceil(rawStep / 5) * 5);
    return Math.ceil(max / step) * step;
  }, [data]);

  const yTicks = useMemo(() => {
    const count = 5;
    const step = maxValue / count;
    return Array.from({ length: count + 1 }, (_, i) => Math.round(i * step));
  }, [maxValue]);

  const yAxisWidth = useMemo(() => {
    return Math.max(36, String(maxValue).length * 8 + 12);
  }, [maxValue]);

  const showEmptyState = !data?.some((item) => item?.raised > 0 || item?.closed > 0);

  return (
    <div className="bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] pt-4 px-4 pb-2 flex flex-col gap-4 min-w-0">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex gap-1 items-center">
            <AnalyticsIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
            <span className="text-base font-medium text-heading-text dark:text-heading-text-dk">
              Monthly {helpdeskLabels.ticketPlural}: Raised vs Closed
            </span>
          </div>
          <p className="text-xs text-dark-gray dark:text-dark-gray-dk mt-0.5">
            Count of {helpdeskLabels.ticketPluralLower} closed in each month
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="flex items-center h-[22px] px-2 py-0.5 rounded-md bg-verba-indigo dark:bg-verba-indigo-dk text-sm font-semibold text-pure-white dark:text-pure-white-dk uppercase leading-normal">
            New
          </span>
          <span className="flex items-center gap-0.5 h-[22px] pl-1 pr-2 py-0.5 rounded-md bg-analytics-green dark:bg-analytics-green-dk text-sm font-semibold text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk uppercase leading-normal">
            <VerifiedIcon className="w-4 h-4 text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk" />
            Closed
          </span>
        </div>
      </div>

      {showEmptyState ? (
        <ChartEmptyState height="h-[280px]" />
      ) : (
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 16, right: 4, bottom: 8, left: 0 }}
              barGap={2}
              barCategoryGap="20%"
            >
              <CartesianGrid
                horizontal={true}
                vertical={false}
                stroke={gridStroke}
              />
              <YAxis
                type="number"
                domain={[0, maxValue]}
                ticks={yTicks}
                tickLine={false}
                axisLine={{ stroke: gridStroke }}
                tick={{
                  fill: isDarkMode ? "#c0c2c5" : "#898989",
                  fontSize: 11,
                  fontWeight: 500,
                }}
                width={yAxisWidth}
              />
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tick={(props: MonthTickProps) => (
                  <MonthTick {...props} isDarkMode={isDarkMode} data={data} />
                )}
                interval={0}
                height={40}
              />
              <Bar
                dataKey="raised"
                fill={raisedFill}
                radius={[6, 6, 0, 0]}
                barSize={20}
                label={(props: ValueLabelProps) => (
                  <ValueLabel {...props} isDarkMode={isDarkMode} />
                )}
                isAnimationActive={false}
              />
              <Bar
                dataKey="closed"
                fill="#10b981"
                radius={[6, 6, 0, 0]}
                barSize={20}
                label={(props: ValueLabelProps) => (
                  <ValueLabel {...props} isDarkMode={isDarkMode} />
                )}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default MonthlyRaisedVsClosed;
